import React from "react";
import { fromJS } from "immutable";
import { SelectOption, Text } from "@showitinc/showit-wc";

import Input from "../../../base/Input";
import FontSelectInput from "../../../../components/FontSelectInput";
import StyleSelectInput from "../../../../components/StyleSelectInput";
import PropertyMaps from "../../../../constants/PropertyMaps";
import CustomizeEffects from "../../shared/CustomizeEffects";

import AlignOptions from "../../../../components/elementproperties/AlignOptions";
import ColumnOptions from "../../../../components/elementproperties/ColumnOptions";
import AppConstants from "../../../../constants/AppConstants";
import When from "../../../../components/utils/When";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import { elementSyncUtils, toggleSyncedProp as toggleSync } from "../../../../utils/SyncUtils";
import { gatherProps } from "../../../../utils/Merge/MergeUtils.mjs";
import {
  TEXT_ALIGNMENT,
  TEXT_COLOR,
  TEXT_COLUMNS,
  TEXT_FONT,
  TEXT_LETTER_CASE,
  TEXT_LETTER_SPACING,
  TEXT_LINE_HEIGHT,
  TEXT_OVERFLOW,
  TEXT_SIZE,
  TEXT_STYLE,
  TEXT_STYLES_LINK_COLOR,
  TEXT_STYLES_LINK_FORMAT,
  TEXT_STYLES_LINK_HOVER_COLOR,
  TEXT_STYLES_LINK_HOVER_FORMAT,
} from "../../../../constants/MergeConstants";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import { SELECT, COLOR_PICKER } from "../../../../constants/InputConstants.mjs";
import { IS_DEVIATION, MIXED } from "../../../../constants/DesignConstants.mjs";
import { prepareInputUtils } from "../../../../utils/InputUtils";

const {
  TEXT_OVERFLOW_DISPLAY,
  TEXT_OVERFLOW_CROP,
  TEXT_OVERFLOW_SINGLE_LINE,
  TEXT_OVERFLOW_SINGLE_LINE_ELLIPSIS,
  LAYOUT_DESKTOP,
} = AppConstants;

const { TEXT_STYLE_PROPS, TEXT_NESTED_STYLES_INLINE_LINK } = PropertyMaps;

const ElementPropertiesStyle = ({
  inlineLinksEnabled = true,
  element = fromJS({}),
  onPropUpdate,
  layout,
  isLocked,
  preparedElement,
  theme,
  mappedElement,
}) => {
  const _baseOnPropChange = (propInfo) => {
    onPropUpdate && onPropUpdate(propInfo);
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    _baseOnPropChange,
    layout,
    undefined,
    undefined,
    theme,
  );

  const mergedProps = gatherProps(
    preparedElement,
    layout,
    [TEXT_STYLE, ...TEXT_STYLE_PROPS, ...TEXT_NESTED_STYLES_INLINE_LINK],
    undefined,
    theme,
  );

  const styleSync = isSynced(TEXT_STYLE);
  const showLinkSettings = inlineLinksEnabled;
  const columnCnt = mergedProps.get(TEXT_COLUMNS);
  const hasPlaceholder = !!element.getIn(["wp", "field"], "");

  // default to active layout if style is not synced. Otherwise pass to propGetter.
  const subStylePropNameGetter = (propName, parentProp) =>
    !styleSync ? `${layout}.${propName}` : getPropName(propName, parentProp);

  const { onChange } = initFormatter(_baseOnPropChange, subStylePropNameGetter);
  const { onChange: styleChange } = initFormatter(_baseOnPropChange, getPropName);

  const separatePath = (path) => path.split(".");
  const prepChange = (prop) => [prop, mergedProps.get(prop)];
  const prepLinkChange = (prop) => [prop, mergedProps.getIn(separatePath(prop))];

  const baseStyle = mergedProps.get(TEXT_STYLE);
  const style =
    baseStyle === MIXED ? baseStyle : `${baseStyle}${preparedElement.get(IS_DEVIATION) ? "+" : ""}`;

  // todo: hide sync toggle for mixed style values
  const addToggle = (propName, isStyle) =>
    (isStyle && !styleSync) || (!isStyle && styleSync)
      ? {
          sync: isSynced(propName),
          toggleSync: () => {
            isStyle
              ? toggleSync(element, _baseOnPropChange, layout)(propName)
              : toggleSyncedProp(propName);
          },
        }
      : {
          sync: true,
        };

  const {
    isMixed,
    canMerge: baseCanMerge,
    isSyncMixed,
    prepareValue,
  } = prepareInputUtils(layout, preparedElement, mergedProps);

  const canMerge = (propName) => styleSync && baseCanMerge(propName);
  const isStyleMergeMixed = isMixed(TEXT_STYLE);

  return (
    <SplitMergeWrapper {...addToggle(TEXT_STYLE, true)} layout={layout} mixed={isStyleMergeMixed}>
      <StyleSelectInput
        label="Style"
        disabled={isLocked}
        value={isStyleMergeMixed ? MIXED : style}
        onSISelect={styleChange(TEXT_STYLE, style)}
        context={!isSynced(TEXT_STYLE) && !isStyleMergeMixed}
        reserveContext
        shift="left"
        type="text"
      />
      {!isStyleMergeMixed && (
        <>
          <SplitMergeWrapper
            {...addToggle(TEXT_COLOR)}
            layout={layout}
            mixed={isSyncMixed(TEXT_COLOR)}
          >
            <Input
              color={prepareValue(TEXT_COLOR)}
              context={canMerge(TEXT_COLOR)}
              disabled={isLocked}
              inputType={COLOR_PICKER}
              label="Color"
              onSIBeforeChange={onChange(...prepChange(TEXT_COLOR))}
              onSIChange={onChange(...prepChange(TEXT_COLOR))}
              onSIInput={onChange(...prepChange(TEXT_COLOR), false)}
            />
          </SplitMergeWrapper>
          <SplitMergeWrapper
            {...addToggle(TEXT_FONT)}
            layout={layout}
            mixed={isSyncMixed(TEXT_FONT)}
          >
            <FontSelectInput
              context={canMerge(TEXT_FONT)}
              disabled={isLocked}
              label="Font"
              onChange={onChange(TEXT_FONT, mergedProps.get(TEXT_FONT))}
              shift="left"
              value={prepareValue(TEXT_FONT)}
            />
          </SplitMergeWrapper>
          <SplitMergeWrapper
            {...addToggle(TEXT_SIZE)}
            layout={layout}
            mixed={isSyncMixed(TEXT_SIZE)}
          >
            <Input
              context={canMerge(TEXT_SIZE)}
              disabled={isLocked}
              label="Size"
              min="1"
              onSIBeforeChange={onChange(...prepChange(TEXT_SIZE))}
              onSIInput={onChange(...prepChange(TEXT_SIZE), false)}
              step="1"
              type={INPUT_TYPE_NUMBER}
              units="px"
              value={prepareValue(TEXT_SIZE)}
            />
          </SplitMergeWrapper>
          <SplitMergeWrapper
            {...addToggle(TEXT_LINE_HEIGHT)}
            layout={layout}
            mixed={isSyncMixed(TEXT_LINE_HEIGHT)}
          >
            <Input
              context={canMerge(TEXT_LINE_HEIGHT)}
              disabled={isLocked}
              label="Line Height"
              min="0.1"
              onSIBeforeChange={onChange(...prepChange(TEXT_LINE_HEIGHT))}
              onSIInput={onChange(...prepChange(TEXT_LINE_HEIGHT), false)}
              precision="3"
              step="0.1"
              type={INPUT_TYPE_NUMBER}
              units="u"
              value={prepareValue(TEXT_LINE_HEIGHT)}
            />
          </SplitMergeWrapper>
          <SplitMergeWrapper
            {...addToggle(TEXT_LETTER_SPACING)}
            layout={layout}
            mixed={isSyncMixed(TEXT_LETTER_SPACING)}
          >
            <Input
              context={canMerge(TEXT_LETTER_SPACING)}
              disabled={isLocked}
              label="Letter Spacing"
              onSIBeforeChange={onChange(...prepChange(TEXT_LETTER_SPACING))}
              onSIInput={onChange(...prepChange(TEXT_LETTER_SPACING), false)}
              precision="3"
              step="0.1"
              type={INPUT_TYPE_NUMBER}
              units="em"
              value={prepareValue(TEXT_LETTER_SPACING)}
            />
          </SplitMergeWrapper>
          <SplitMergeWrapper
            {...addToggle(TEXT_LETTER_CASE)}
            layout={layout}
            mixed={isSyncMixed(TEXT_LETTER_CASE)}
          >
            <Input
              context={canMerge(TEXT_LETTER_CASE)}
              disabled={isLocked}
              inputType={SELECT}
              label="Letter Case"
              onSISelect={onChange(TEXT_LETTER_CASE, mergedProps.get(TEXT_LETTER_CASE, ""))}
              reserveContext
              shift="left"
              value={prepareValue(TEXT_LETTER_CASE)}
            >
              <SelectOption value="none">Normal</SelectOption>
              <SelectOption value="uppercase">UPPERCASE</SelectOption>
              <SelectOption value="lowercase">lowercase</SelectOption>
              <SelectOption value="capitalize">Title Case</SelectOption>
            </Input>
          </SplitMergeWrapper>

          <SplitMergeWrapper
            {...addToggle(TEXT_ALIGNMENT)}
            layout={layout}
            mixed={isSyncMixed(TEXT_ALIGNMENT)}
          >
            <AlignOptions
              context={canMerge(TEXT_ALIGNMENT)}
              label="Text Align"
              propName={getPropName(TEXT_ALIGNMENT)}
              alignment={prepareValue(TEXT_ALIGNMENT)}
              disabled={isLocked}
              onSelect={onChange(TEXT_ALIGNMENT, mergedProps.get(TEXT_ALIGNMENT, "left"))}
              {...addToggle(TEXT_ALIGNMENT)}
            />
          </SplitMergeWrapper>
          <When it={layout === LAYOUT_DESKTOP}>
            <ColumnOptions
              disabled={hasPlaceholder || isLocked}
              columns={columnCnt}
              onPropChange={_baseOnPropChange}
              element={element}
            />
            <When it={!!columnCnt}>
              <Input
                type={INPUT_TYPE_NUMBER}
                disabled={hasPlaceholder || isLocked}
                label="Column Gap"
                min="0"
                onSIBeforeChange={onChange("columnGap", mergedProps.get("columnGap"))}
                onSIInput={onChange("columnGap", mergedProps.get("columnGap"), false)}
                step="1"
                units="px"
                value={prepareValue("columnGap")}
              />
            </When>
          </When>
          <When it={!columnCnt}>
            <SplitMergeWrapper
              {...addToggle(TEXT_OVERFLOW)}
              layout={layout}
              mixed={isSyncMixed(TEXT_OVERFLOW, preparedElement)}
            >
              <Input
                context={canMerge(TEXT_OVERFLOW)}
                disabled={!!columnCnt || isLocked}
                inputType={SELECT}
                label="Overflow"
                onSISelect={onChange(TEXT_OVERFLOW, mergedProps.get(TEXT_OVERFLOW, ""))}
                shift="left"
                value={prepareValue(TEXT_OVERFLOW)}
              >
                <SelectOption value={TEXT_OVERFLOW_DISPLAY}>Display</SelectOption>
                <SelectOption value={TEXT_OVERFLOW_SINGLE_LINE}>Single Line</SelectOption>
                <SelectOption value={TEXT_OVERFLOW_SINGLE_LINE_ELLIPSIS}>
                  Single Line with ellipses
                </SelectOption>
                <SelectOption value={TEXT_OVERFLOW_CROP}>Crop</SelectOption>
              </Input>
            </SplitMergeWrapper>
          </When>

          {showLinkSettings && (
            <>
              <Text className="panel-subheading">Inline Links</Text>
              <SplitMergeWrapper
                {...addToggle(TEXT_STYLES_LINK_COLOR)}
                layout={layout}
                mixed={isSyncMixed(TEXT_STYLES_LINK_COLOR)}
              >
                <Input
                  color={prepareValue(TEXT_STYLES_LINK_COLOR)}
                  context={canMerge(TEXT_STYLES_LINK_COLOR)}
                  disabled={isLocked}
                  inputType={COLOR_PICKER}
                  label="Color"
                  onSIBeforeChange={onChange(...prepLinkChange(TEXT_STYLES_LINK_COLOR))}
                  onSIChange={onChange(...prepLinkChange(TEXT_STYLES_LINK_COLOR))}
                  onSIInput={onChange(...prepLinkChange(TEXT_STYLES_LINK_COLOR), false)}
                />
              </SplitMergeWrapper>
              <SplitMergeWrapper
                {...addToggle(TEXT_STYLES_LINK_FORMAT)}
                layout={layout}
                mixed={isSyncMixed(TEXT_STYLES_LINK_FORMAT)}
              >
                <Input
                  context={canMerge(TEXT_STYLES_LINK_FORMAT)}
                  disabled={isLocked}
                  inputType={SELECT}
                  label="Format"
                  reserveContext
                  shift="left"
                  value={prepareValue(TEXT_STYLES_LINK_FORMAT)}
                  onSISelect={onChange(...prepLinkChange(TEXT_STYLES_LINK_FORMAT))}
                >
                  <SelectOption value="underline">Underline</SelectOption>
                  <SelectOption value="none">No Underline</SelectOption>
                </Input>
              </SplitMergeWrapper>
              <SplitMergeWrapper
                {...addToggle(TEXT_STYLES_LINK_HOVER_COLOR)}
                layout={layout}
                mixed={isSyncMixed(TEXT_STYLES_LINK_HOVER_COLOR)}
              >
                <Input
                  color={prepareValue(TEXT_STYLES_LINK_HOVER_COLOR)}
                  context={canMerge(TEXT_STYLES_LINK_HOVER_COLOR)}
                  disabled={isLocked}
                  inputType={COLOR_PICKER}
                  label="Hover Color"
                  onSIBeforeChange={onChange(...prepLinkChange(TEXT_STYLES_LINK_HOVER_COLOR))}
                  onSIChange={onChange(...prepLinkChange(TEXT_STYLES_LINK_HOVER_COLOR))}
                  onSIInput={onChange(...prepLinkChange(TEXT_STYLES_LINK_HOVER_COLOR), false)}
                />
              </SplitMergeWrapper>
              <SplitMergeWrapper
                {...addToggle(TEXT_STYLES_LINK_HOVER_FORMAT)}
                layout={layout}
                mixed={isSyncMixed(TEXT_STYLES_LINK_HOVER_FORMAT)}
              >
                <Input
                  context={canMerge(TEXT_STYLES_LINK_HOVER_FORMAT)}
                  disabled={isLocked}
                  inputType={SELECT}
                  label="Hover Format"
                  onSISelect={onChange(...prepLinkChange(TEXT_STYLES_LINK_HOVER_FORMAT))}
                  reserveContext
                  shift="left"
                  value={prepareValue(TEXT_STYLES_LINK_HOVER_FORMAT)}
                >
                  <SelectOption value="underline">Underline</SelectOption>
                  <SelectOption value="none">No Underline</SelectOption>
                </Input>
              </SplitMergeWrapper>
            </>
          )}
        </>
      )}
      <CustomizeEffects
        mappedElement={mappedElement}
        layout={layout}
        onPropUpdate={onPropUpdate}
        preparedElement={preparedElement}
        theme={theme}
      />
    </SplitMergeWrapper>
  );
};

export default ElementPropertiesStyle;
